import React, { useEffect, useState, useMemo, forwardRef } from "react";
import axios from "axios";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import BASE_URL from "BASE_URL";

import {
  Card,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Tooltip,
  Box,
  Stack,
  TextField,
  Avatar,
  Slide,
  Grow,
  Zoom,
  Fade,
  Paper,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from "@mui/material";
import { keyframes } from "@mui/system";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import UpdateIcon from "@mui/icons-material/Update";
import ImageIcon from "@mui/icons-material/Image";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SlideshowIcon from "@mui/icons-material/Slideshow";

// Premium Animations
const slideUp = keyframes`
  from { transform: translateY(100px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(33, 150, 243, 0); }
  100% { box-shadow: 0 0 0 0 rgba(33, 150, 243, 0); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
  50% { box-shadow: 0 0 25px rgba(33, 150, 243, 0.6); }
  100% { box-shadow: 0 0 5px rgba(33, 150, 243, 0.2); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.2); }
  70% { transform: scale(0.9); }
  100% { transform: scale(1); opacity: 1; }
`;

const shakeAnimation = keyframes`
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
  20%, 40%, 60%, 80% { transform: translateX(2px); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-5px); }
`;

// Transition component
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} timeout={500} />;
});

function HomepageBannerManagement() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [formMode, setFormMode] = useState("add");
  const [selectedId, setSelectedId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Form states
  const [selectedImages, setSelectedImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [existingPublicIds, setExistingPublicIds] = useState([]);
  const [removedImages, setRemovedImages] = useState([]);

  const token = localStorage.getItem("token");
  const metaBlue = "#1565C0";

  // Fetch all banners
  const fetchBanners = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/homepage-banner/get`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setBanners(response.data.data);
      } else {
        showSnackbar(response.data.message || "Failed to fetch banners", "error");
      }
    } catch (error) {
      console.error("Error fetching banners:", error);
      showSnackbar(error.response?.data?.message || "Error fetching banners", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Handle multiple image selection
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const validTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];
    const maxSize = 2 * 1024 * 1024; // 2MB

    const validFiles = files.filter((file) => {
      if (!validTypes.includes(file.type)) {
        showSnackbar(`${file.name} is not a valid image type`, "warning");
        return false;
      }
      if (file.size > maxSize) {
        showSnackbar(`${file.name} exceeds 2MB limit`, "warning");
        return false;
      }
      return true;
    });

    setSelectedImages((prev) => [...prev, ...validFiles]);

    // Create previews
    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviews((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveNewImage = (index) => {
    setSelectedImages((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = (publicId, imageUrl, index) => {
    setRemovedImages((prev) => [...prev, publicId]);
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
    setExistingPublicIds((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle form dialog open
  const handleOpenDialog = (mode = "add", item = null) => {
    setFormMode(mode);
    if (mode === "edit" && item) {
      setSelectedId(item._id);
      setExistingImages(item.images || []);
      setExistingPublicIds(item.image_public_id || []);
      setSelectedImages([]);
      setImagePreviews([]);
      setRemovedImages([]);
    } else {
      resetForm();
    }
    setOpenForm(true);
  };

  // Handle view dialog open
  const handleViewDialog = (item) => {
    setSelectedBanner(item);
    setViewDialogOpen(true);
  };

  // Reset form
  const resetForm = () => {
    setSelectedImages([]);
    setImagePreviews([]);
    setExistingImages([]);
    setExistingPublicIds([]);
    setRemovedImages([]);
    setSelectedId(null);
  };

  // Handle form submit
  const handleSubmit = async () => {
    if (formMode === "add" && selectedImages.length === 0) {
      showSnackbar("Please select at least one image", "warning");
      return;
    }
    if (formMode === "edit" && selectedImages.length === 0 && existingImages.length === 0) {
      showSnackbar("Please select at least one image", "warning");
      return;
    }

    setIsSubmitting(true);
    try {
      let response;
      const formDataToSend = new FormData();

      if (formMode === "add") {
        selectedImages.forEach((image) => {
          formDataToSend.append("images", image);
        });
        response = await axios.post(`${BASE_URL}/homepage-banner/create`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        selectedImages.forEach((image) => {
          formDataToSend.append("images", image);
        });
        removedImages.forEach((publicId) => {
          formDataToSend.append("removeImages[]", publicId);
        });
        response = await axios.put(
          `${BASE_URL}/homepage-banner/update?id=${selectedId}`,
          formDataToSend,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
      }

      if (response.data.success) {
        showSnackbar(`Banner ${formMode === "add" ? "added" : "updated"} successfully!`, "success");
        fetchBanners();
        setOpenForm(false);
        resetForm();
      } else {
        showSnackbar(response.data.message || "Operation failed", "error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showSnackbar(error.response?.data?.message || "Operation failed", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete dialog open
  const handleOpenDeleteDialog = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  // Handle delete banner
  const handleDeleteBanner = async () => {
    setIsSubmitting(true);
    try {
      const response = await axios.delete(`${BASE_URL}/homepage-banner/delete?id=${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        showSnackbar("Banner deleted successfully!", "success");
        fetchBanners();
        setDeleteDialogOpen(false);
      } else {
        showSnackbar(response.data.message || "Delete failed", "error");
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
      showSnackbar(error.response?.data?.message || "Failed to delete banner", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const columns = [
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          S.No
        </MDTypography>
      ),
      accessor: "sno",
      align: "center",
      width: "5%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          IMAGES
        </MDTypography>
      ),
      accessor: "images",
      align: "center",
      width: "60%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          TOTAL IMAGES
        </MDTypography>
      ),
      accessor: "totalImages",
      align: "center",
      width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          CREATED
        </MDTypography>
      ),
      accessor: "createdAt",
      align: "center",
      width: "15%",
    },
    {
      Header: (
        <MDTypography variant="caption" fontWeight="bold">
          ACTIONS
        </MDTypography>
      ),
      accessor: "action",
      align: "center",
      width: "15%",
    },
  ];

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const rows = banners.map((banner, index) => ({
    sno: (
      <MDTypography variant="caption" fontWeight="medium">
        {index + 1}
      </MDTypography>
    ),
    images: (
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "center" }}>
        {banner.images?.slice(0, 3).map((img, idx) => (
          <Avatar
            key={idx}
            src={img}
            variant="rounded"
            sx={{
              width: 50,
              height: 50,
              borderRadius: "8px",
              transition: "transform 0.3s",
              "&:hover": { transform: "scale(1.1)" },
            }}
          />
        ))}
        {banner.images?.length > 3 && <Chip label={`+${banner.images.length - 3}`} size="small" />}
      </Box>
    ),
    totalImages: (
      <Chip
        label={`${banner.images?.length || 0} Images`}
        size="small"
        sx={{ bgcolor: "#e3f2fd", color: metaBlue, fontWeight: "bold" }}
      />
    ),
    createdAt: <MDTypography variant="caption">{formatDate(banner.createdAt)}</MDTypography>,
    action: (
      <MDBox display="flex" justifyContent="center" gap={0.5}>
        <Tooltip title="View Banner">
          <IconButton
            color="default"
            onClick={() => handleViewDialog(banner)}
            size="small"
            sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit Banner">
          <IconButton
            color="info"
            onClick={() => handleOpenDialog("edit", banner)}
            size="small"
            sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {/* <Tooltip title="Delete Banner">
          <IconButton
            color="error"
            onClick={() => handleOpenDeleteDialog(banner._id)}
            size="small"
            sx={{ transition: "all 0.3s", "&:hover": { transform: "scale(1.1)" } }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip> */}
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3} sx={{ animation: `${slideUp} 0.5s ease-out` }}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: "24px" }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
              >
                <MDBox display="flex" alignItems="center" gap={1}>
                  <SlideshowIcon sx={{ color: "#fff", fontSize: 28 }} />
                  <MDTypography variant="h6" color="white">
                    Homepage Banner Management
                  </MDTypography>
                </MDBox>
                {/* <MDButton
                  variant="gradient"
                  color="dark"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog("add")}
                  sx={{ borderRadius: "12px", textTransform: "none" }}
                >
                  Add Banner
                </MDButton> */}
              </MDBox>

              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : banners.length === 0 ? (
                  <MDBox display="flex" justifyContent="center" py={5}>
                    <MDTypography variant="h6" color="dark" fontWeight="medium">
                      No banners found. Add your first banner.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* ADD/EDIT DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => !isSubmitting && setOpenForm(false)}
        fullWidth
        maxWidth="md"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #2196f3, #64b5f6, #2196f3)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setOpenForm(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(33, 150, 243, 0.1)",
            },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon />
        </IconButton>

        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(33,150,243,0.08) 0%, rgba(33,150,243,0) 70%)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />

        <DialogTitle sx={{ pb: 1, pt: 3, position: "relative", zIndex: 1 }}>
          <Grow in={openForm} timeout={600}>
            <MDBox display="flex" alignItems="center" gap={2}>
              <Zoom in={openForm} timeout={600}>
                <MDBox
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                    width: 56,
                    height: 56,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
                    animation: `${glow} 2s ease-in-out infinite`,
                  }}
                >
                  {formMode === "add" ? (
                    <AddIcon sx={{ fontSize: 32, color: "white" }} />
                  ) : (
                    <UpdateIcon sx={{ fontSize: 32, color: "white" }} />
                  )}
                </MDBox>
              </Zoom>
              <MDBox>
                <MDTypography
                  variant="h4"
                  fontWeight="bold"
                  sx={{
                    background: "linear-gradient(135deg, #2196f3 0%, #1565c0 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  {formMode === "add" ? "Add Banner Images" : "Update Banner Images"}
                </MDTypography>
                <MDTypography variant="body2" color="textSecondary">
                  {formMode === "add"
                    ? "Upload banner images for homepage"
                    : "Manage your banner images"}
                </MDTypography>
              </MDBox>
            </MDBox>
          </Grow>
        </DialogTitle>

        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={openForm} timeout={800}>
            <MDBox pt={3} pb={2}>
              <Grid container spacing={3}>
                {/* Existing Images */}
                {formMode === "edit" && existingImages.length > 0 && (
                  <Grid item xs={12}>
                    <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                      Current Images
                    </MDTypography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {existingImages.map((img, idx) => (
                        <Zoom in={true} timeout={500} key={idx}>
                          <Paper
                            elevation={0}
                            sx={{
                              position: "relative",
                              width: 150,
                              borderRadius: "12px",
                              overflow: "hidden",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <img
                              src={img}
                              alt={`Banner ${idx + 1}`}
                              style={{ width: "100%", height: 120, objectFit: "cover" }}
                            />
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleRemoveExistingImage(existingPublicIds[idx], img, idx)
                              }
                              sx={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                bgcolor: "rgba(0,0,0,0.6)",
                                color: "#fff",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        </Zoom>
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* New Images Preview */}
                {imagePreviews.length > 0 && (
                  <Grid item xs={12}>
                    <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                      New Images
                    </MDTypography>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      {imagePreviews.map((preview, idx) => (
                        <Zoom in={true} timeout={500} key={idx}>
                          <Paper
                            elevation={0}
                            sx={{
                              position: "relative",
                              width: 150,
                              borderRadius: "12px",
                              overflow: "hidden",
                              border: "1px solid #e0e0e0",
                            }}
                          >
                            <img
                              src={preview}
                              alt={`Preview ${idx + 1}`}
                              style={{ width: "100%", height: 120, objectFit: "cover" }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveNewImage(idx)}
                              sx={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                bgcolor: "rgba(0,0,0,0.6)",
                                color: "#fff",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Paper>
                        </Zoom>
                      ))}
                    </Box>
                  </Grid>
                )}

                {/* Image Upload */}
                <Grid item xs={12}>
                  <Zoom in={openForm} timeout={600} style={{ transitionDelay: "300ms" }}>
                    <Box>
                      <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                        Upload Banner Images
                      </MDTypography>
                      <Paper
                        elevation={0}
                        sx={{
                          border: "2px dashed #e0e0e0",
                          borderRadius: "16px",
                          p: 3,
                          textAlign: "center",
                          bgcolor: "#fafafa",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          "&:hover": {
                            borderColor: "#2196f3",
                            bgcolor: "#f5f5f5",
                            transform: "scale(1.02)",
                          },
                        }}
                        onClick={() => document.getElementById("image-upload").click()}
                      >
                        <input
                          id="image-upload"
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageChange}
                          style={{ display: "none" }}
                        />
                        <CloudUploadIcon sx={{ fontSize: 48, color: "#9e9e9e", mb: 1 }} />
                        <MDTypography variant="body2" color="text">
                          Click to upload or drag and drop
                        </MDTypography>
                        <MDTypography variant="caption" color="text" display="block">
                          PNG, JPG, JPEG, WEBP (Max 2MB each) | Multiple images allowed
                        </MDTypography>
                      </Paper>
                    </Box>
                  </Zoom>
                </Grid>
              </Grid>
            </MDBox>
          </Fade>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2,
            gap: 2,
            justifyContent: "flex-end",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "400ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => {
                setOpenForm(false);
                resetForm();
              }}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 3,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                color: "#111",
                borderColor: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>
          <Zoom in={openForm} timeout={600} style={{ transitionDelay: "500ms" }}>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />
              }
            >
              {isSubmitting ? "PROCESSING..." : formMode === "add" ? "SAVE" : "UPDATE"}
            </MDButton>
          </Zoom>
        </DialogActions>
      </Dialog>

      {/* VIEW BANNER DIALOG */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="lg"
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "28px",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
          },
        }}
      >
        <IconButton
          onClick={() => setViewDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "rotate(90deg) scale(1.1)",
              bgcolor: "rgba(33, 150, 243, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ pb: 1, pt: 3, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Zoom in={viewDialogOpen} timeout={600}>
            <MDBox display="flex" flexDirection="column" alignItems="center" gap={2}>
              <MDBox
                sx={{
                  background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 8px 20px rgba(33, 150, 243, 0.3)",
                  animation: `${float} 2s ease-in-out infinite`,
                }}
              >
                <SlideshowIcon sx={{ fontSize: 45, color: "white" }} />
              </MDBox>
              <MDTypography variant="h4" fontWeight="bold">
                Banner Images
              </MDTypography>
              <Chip label={`${selectedBanner?.images?.length || 0} Images`} color="info" />
            </MDBox>
          </Zoom>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={viewDialogOpen} timeout={800}>
            <Box sx={{ py: 2 }}>
              <ImageList variant="masonry" cols={3} gap={16}>
                {selectedBanner?.images?.map((img, idx) => (
                  <ImageListItem key={idx}>
                    <img
                      src={img}
                      alt={`Banner ${idx + 1}`}
                      style={{ borderRadius: "12px", cursor: "pointer" }}
                      onClick={() => window.open(img, "_blank")}
                    />
                    <ImageListItemBar
                      title={`Image ${idx + 1}`}
                      position="below"
                      sx={{ borderRadius: "12px", mt: 1 }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, justifyContent: "center" }}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => setViewDialogOpen(false)}
            sx={{ borderRadius: "12px", px: 4 }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isSubmitting && setDeleteDialogOpen(false)}
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: "32px",
            background: "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)",
            boxShadow: "0 40px 80px rgba(0, 0, 0, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
              animation: `${shimmer} 2s infinite linear`,
              backgroundSize: "200% 100%",
            },
          },
        }}
      >
        <IconButton
          onClick={() => !isSubmitting && setDeleteDialogOpen(false)}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": { transform: "rotate(90deg) scale(1.1)", bgcolor: "rgba(239, 68, 68, 0.1)" },
          }}
          disabled={isSubmitting}
        >
          <CloseIcon sx={{ color: "#ef4444" }} />
        </IconButton>
        <Box
          sx={{
            position: "absolute",
            top: -50,
            left: -50,
            right: -50,
            bottom: -50,
            background: "radial-gradient(circle, rgba(239,68,68,0.08) 0%, rgba(239,68,68,0) 70%)",
            pointerEvents: "none",
          }}
        />
        <DialogTitle sx={{ pt: 4, pb: 2, textAlign: "center", position: "relative", zIndex: 1 }}>
          <Grow in={deleteDialogOpen} timeout={600}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Zoom in={deleteDialogOpen} timeout={600}>
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                    borderRadius: "50%",
                    animation: `${pulseRing} 2s infinite`,
                    cursor: "pointer",
                    mb: 2,
                    "&:hover": { animation: `${shakeAnimation} 0.5s ease-in-out` },
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(239, 68, 68, 0.4)",
                      animation: `${glow} 2s ease-in-out infinite`,
                    }}
                  >
                    <WarningAmberIcon sx={{ fontSize: 40, color: "white" }} />
                  </Box>
                </Box>
              </Zoom>
              <Box sx={{ animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)` }}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                  }}
                >
                  <MDTypography variant="h4" fontWeight="bold">
                    Delete Banner?
                  </MDTypography>
                </Box>
              </Box>
            </Box>
          </Grow>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", zIndex: 1 }}>
          <Fade in={deleteDialogOpen} timeout={800}>
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "100ms" }}>
                <Alert
                  severity="warning"
                  icon={<WarningAmberIcon />}
                  sx={{
                    borderRadius: "16px",
                    mb: 3,
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                  }}
                >
                  <MDTypography variant="body2" fontWeight="medium" color="error">
                    This action cannot be undone!
                  </MDTypography>
                </Alert>
              </Zoom>
              <Grow in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "150ms" }}>
                <Paper
                  elevation={0}
                  sx={{
                    background:
                      "linear-gradient(135deg, rgba(239,68,68,0.05) 0%, rgba(239,68,68,0.02) 100%)",
                    borderRadius: "20px",
                    p: 3,
                    border: "1px solid rgba(239, 68, 68, 0.2)",
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "scale(1.02)", borderColor: "rgba(239, 68, 68, 0.4)" },
                  }}
                >
                  <MDTypography
                    variant="body2"
                    color="textSecondary"
                    gutterBottom
                    fontWeight="bold"
                  >
                    BANNER TO BE DELETED
                  </MDTypography>
                  <MDTypography
                    variant="h6"
                    fontWeight="bold"
                    sx={{ color: "#ef4444", wordBreak: "break-word" }}
                  >
                    All {selectedBanner?.images?.length || 0} images will be removed
                  </MDTypography>
                </Paper>
              </Grow>
            </Box>
          </Fade>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 4,
            pt: 2,
            gap: 2,
            justifyContent: "center",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "250ms" }}>
            <MDButton
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                color: "#111",
                borderColor: "#111",
                "&:hover": { transform: "translateY(-2px)", borderColor: "#111" },
              }}
            >
              Cancel
            </MDButton>
          </Zoom>
          <Zoom in={deleteDialogOpen} timeout={600} style={{ transitionDelay: "300ms" }}>
            <MDButton
              variant="contained"
              color="error"
              onClick={handleDeleteBanner}
              disabled={isSubmitting}
              fullWidth
              sx={{
                borderRadius: "14px",
                py: 1.5,
                fontWeight: "bold",
                textTransform: "none",
                transition: "all 0.3s",
                "&:hover": { transform: "translateY(-2px)" },
              }}
              startIcon={
                isSubmitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />
              }
            >
              {isSubmitting ? "DELETING..." : "DELETE PERMANENTLY"}
            </MDButton>
          </Zoom>
        </DialogActions>
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "3px",
            background: "linear-gradient(90deg, #ef4444, #f87171, #ef4444)",
            animation: `${shimmer} 2s infinite linear`,
            backgroundSize: "200% 100%",
          }}
        />
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Slide}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", borderRadius: "12px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

export default HomepageBannerManagement;
